import { instrumentRunQueryKeys, orgQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import {
  ORGS_QUERY_GC_TIME,
  ORGS_QUERY_STALE_TIME,
  STUDIES_QUERY_GC_TIME,
  STUDIES_QUERY_STALE_TIME,
} from '@/constants';
import { useApiClient } from '@/hooks/useApiClient';
import SampleManager from '@/pages/Instrument_Runs/components/SampleManager/SampleManager';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import {
  INSTRUMENT_RUN_TYPE_OPTIONS,
  type InstrumentRunDraftCreate,
  type InstrumentRunDraftUpdate,
  type InstrumentRunDataRow,
  type InstrumentRunForm,
  type InstrumentRunFormValues,
  type InstrumentRunPublic,
  InstrumentRunSampleSize,
  type InstrumentRunSpecies,
  type InstrumentRunType,
  type Org,
  type PaginatedStudiesResponse,
} from '@/types';
import { FullLoading } from '@components/index';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Group } from '@nautilusbio/nb-design-system/Group';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Select } from '@nautilusbio/nb-design-system/Select';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createLazyFileRoute, useLoaderData, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { CloudUploadIcon, SaveIcon } from 'lucide-react';
import React, { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import { useShallow } from 'zustand/react/shallow';

import styles from '../../pages/Experiments/New/styles/styles.module.css';
import '../../pages/Experiments/New/styles/table.css';
import { areAllSampleFieldsFilled, isSampleCountCorrect } from '../../pages/Instrument_Runs/helpers';
import irStyles from '../../pages/Instrument_Runs/styles.module.css';
import globalStyles from '../../styles/index.module.css';

type FieldsArr = Array<{
  containerClassName?: string;
  defaultValue: string;
  name: string;
  label: string;
  type: string;
  hide?: boolean;
  validation?: (value: string) => string | undefined;
  options?:
    | Array<{
        id: string;
        name: string;
      }>
    | string[];
}>;

export const Route = createLazyFileRoute('/instrument_runs/new')({
  component: RouteComponent,
  pendingComponent: () => {
    const { t } = useTranslation();

    return <FullLoading label={t('loading')} />;
  },
});

const defaultValues: InstrumentRunFormValues = {
  name: '',
  description: '',
  type: 'Tau',
  species: 'Human',
  noOfSamples: '4',
  samples: [],
};

const allowedTypes = INSTRUMENT_RUN_TYPE_OPTIONS;
const allowedSpecies = ['Human', 'Mouse'] as const;

const buttonIconSize = 24;

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization));
  const search = useSearch({ from: '/instrument_runs/new' });

  const schema = useMemo(
    () =>
      z
        .object({
          name: z
            .string()
            .min(1, t('create:run.validation.nameRequired'))
            .refine(value => !/['`]/.test(value), {
              message: t('create:run.validation.nameInvalidChars'),
            }),
          description: z.string().optional(),
          type: z.enum(allowedTypes),
          species: z.enum(allowedSpecies),
          noOfSamples: z.string().min(1, t('create:run.validation.numberOfSamplesRequired')),
          samples: z.array(
            z.object({
              name: z.string().min(1, t('create:run.validation.sampleNameRequired')),
              study_name: z.string().min(1, t('create:run.validation.studyNameRequired')),
              org_name: z.string().min(1, t('create:run.validation.orgRequired')),
              well_position: z.string().min(1, t('create:run.validation.wellPositionRequired')),
              demux_index: z.number(),
            }),
          ),
        })
        .refine(
          data => {
            const expectedSamples = Number(data.noOfSamples);
            return isSampleCountCorrect(data.samples, expectedSamples);
          },
          {
            message: t('create:run.validation.uploadedSamplesMismatch'),
            path: ['samples'],
          },
        )
        .refine(data => areAllSampleFieldsFilled(data.samples), {
          message: t('create:run.validation.allSampleFieldsRequired'),
          path: ['samples'],
        }),
    [t],
  );

  // Get loader data - backend is the source of truth
  const loaderData = useLoaderData({ from: '/instrument_runs/new' }) as {
    orgId: string;
    mode: 'create' | 'draft' | 'edit';
    formInstrumentRunData: InstrumentRunPublic | null;
  };
  const { orgId, mode, formInstrumentRunData } = loaderData;

  // Create a unique key for the form based on the run ID
  // This forces a complete remount when navigating between different runs
  const formKey = useMemo(() => {
    return search.id ? `${mode}-${search.id}` : `${mode}-new`;
  }, [mode, search.id]);

  // Guard against missing orgId (should not happen due to beforeLoad, but be defensive)
  if (!orgId) {
    console.error('No orgId from loader, redirecting to organizations');
    navigate({ to: '/organizations' });
    return null;
  }

  // Use suspense query to work seamlessly with the route loader
  const { data: studiesData } = useSuspenseQuery<PaginatedStudiesResponse>({
    queryKey: studyQueryKeys.list(orgId),
    queryFn: async () => {
      return searchStudies(orgId, {
        skip: 0,
        limit: 500, // API maximum limit - orgs with 500+ studies will only see most recent 500
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },
    staleTime: STUDIES_QUERY_STALE_TIME,
    gcTime: STUDIES_QUERY_GC_TIME,
  });

  // Defensive check: ensure we have valid studiesData with records array
  const studies = useMemo(() => {
    if (!studiesData) {
      console.warn('studiesData is undefined');
      return [];
    }
    if (!Array.isArray(studiesData.records)) {
      console.warn('studiesData.records is not an array:', studiesData);
      return [];
    }
    return studiesData.records;
  }, [studiesData]);

  // Fetch organizations to enable org_name to org_id mapping
  const { data: organizations = [] } = useQuery<Org[]>({
    queryKey: orgQueryKeys.list(),
    queryFn: fetchOrganizations,
    staleTime: ORGS_QUERY_STALE_TIME,
    gcTime: ORGS_QUERY_GC_TIME,
  });

  // Create a map for quick org_name to org_id lookup
  const orgNameToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) {
      map.set(org.name, String(org.id));
    }
    return map;
  }, [organizations]);

  // Create reverse mapping from org_id to org_name
  const orgIdToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) {
      map.set(String(org.id), org.name);
    }

    return map;
  }, [organizations]);

  const initialValues = useMemo(() => {
    if (formInstrumentRunData && (mode === 'draft' || mode === 'edit')) {
      // Inline the mapping logic here to ensure orgIdToName is always fresh
      const samples =
        (formInstrumentRunData.samples || []).map(sample => {
          let orgName = sample.org_name || '';
          const studyName = sample.study_name || '';

          // If org_name is missing but org_id is present, look it up
          if (!orgName && sample.org_id) {
            orgName = orgIdToName.get(String(sample.org_id)) || '';
          }

          return {
            name: sample.name || '',
            study_name: studyName,
            org_name: orgName,
            org_id: sample.org_id || '',
            well_position: sample.well_position || '',
            demux_index: sample.demux_index ?? 0,
          };
        }) || [];

      return {
        name: formInstrumentRunData.name || '',
        description: formInstrumentRunData.description || '',
        type: (formInstrumentRunData.type || 'Tau') as InstrumentRunType,
        species: (formInstrumentRunData.species || 'Human') as InstrumentRunSpecies,
        noOfSamples: String(formInstrumentRunData.num_samples || 4),
        samples,
      };
    }
    // Default values for create mode
    return defaultValues;
  }, [formInstrumentRunData, mode, orgIdToName]);

  const fieldConfigurations: FieldsArr = [
    {
      containerClassName: styles.name,
      defaultValue: '',
      name: 'name',
      label: t('create:experiment.fields.name'),
      type: 'text',
      validation: (value: string) => {
        if (!value) return t('create:run.validation.nameRequired');
        // Check for invalid characters (apostrophes and other special chars that might cause issues)
        // Apostrophes (') and backticks (`) are disallowed to prevent SQL injection and due to API limitations.
        if (/['`]/.test(value)) {
          return t('create:run.validation.nameInvalidChars');
        }
        return undefined;
      },
    },
    {
      defaultValue: '',
      name: 'description',
      label: t('create:experiment.fields.description'),
      type: 'textarea',
    },
  ];

  const selectFields: FieldsArr = [
    {
      defaultValue: 'Tau',
      name: 'type',
      label: t('create:experiment.fields.assayType.label'),
      type: 'select',
      options: [
        t('create:experiment.fields.assayType.options.broadscale'),
        t('create:experiment.fields.assayType.options.tau'),
      ],
    },
    {
      defaultValue: 'Human',
      name: 'species',
      label: t('create:experiment.fields.species.label'),
      type: 'select',
      options: [
        t('create:experiment.fields.species.options.human'),
        t('create:experiment.fields.species.options.mouse'),
      ],
    },
    {
      defaultValue: '12',
      name: 'noOfSamples',
      label: t('create:experiment.fields.noOfSamples.label'),
      type: 'select',
      options: ['4', '12', '24', '36'],
    },
  ];

  // Reusable error handler for instrument run API errors
  const handleInstrumentRunError = (
    error: unknown,
    defaultErrorKey: 'create:run.failed' | 'create:run.failedToSaveDraft',
  ) => {
    console.error('Instrument run error:', error);

    // Try to extract more detailed error information
    let errorMessage: string = t(defaultErrorKey);

    if (error && typeof error === 'object') {
      // Check for 409 Conflict errors (duplicate sample names)
      if ('status' in error && error.status === 409) {
        // Extract the detail message from the error response data
        if ('data' in error && error.data && typeof error.data === 'object') {
          const errorData = error.data as Record<string, unknown>;
          if ('detail' in errorData && typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else {
            errorMessage = t('create:run.validation.sampleNameConflict');
          }
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message;
        } else {
          errorMessage = t('create:run.validation.sampleNameConflict');
        }

        // Show a more prominent error for sample conflicts
        toast.error(errorMessage, {
          duration: 8000, // Show longer for user to read
        });
        return;
      }
      // Handle other errors
      if ('message' in error) {
        errorMessage = (error as Error).message;
      }
    }

    toast.error(errorMessage);
  };

  const form = useForm({
    defaultValues: initialValues,
    validators: {
      onChange: ({ value }) => {
        const result = schema.safeParse(value);
        if (!result.success) {
          return result.error.format();
        }
        return undefined;
      },
    },
    onSubmit: async ({ value }) => {
      if (!selectedOrg) {
        toast.error(t('create:run.noOrgSelected'));
        return;
      }

      // Validate name
      const nameValidation = fieldConfigurations.find(f => f.name === 'name')?.validation;

      if (nameValidation) {
        const error = nameValidation(value.name);

        if (error) {
          toast.error(error);

          return;
        }
      }

      try {
        // Determine if we're updating an existing run (draft or edit mode with ID)
        const isUpdating = (mode === 'draft' || mode === 'edit') && !!search.id;

        // Convert form data to API format, mapping org_name to org_id
        const submitInstrumentRunData: InstrumentRunDraftUpdate = {
          name: value.name,
          description: value.description || null,
          type: value.type as InstrumentRunType,
          species: value.species as InstrumentRunSpecies,
          num_samples: Number(value.noOfSamples) as InstrumentRunSampleSize,
          samples: value.samples.map((sample: InstrumentRunDataRow) => {
            // Look up org_id from org_name, ensuring org_name is a string
            const orgName = sample.org_name ?? '';
            const sampleOrgId = orgNameToId.get(orgName);
            if (!sampleOrgId && orgName) {
              console.warn(`Could not find org_id for org_name: ${orgName}`);
            }
            return {
              name: sample.name,
              study_name: sample.study_name ?? '',
              org_id: sampleOrgId || null,
              org_name: orgName,
              well_position: sample.well_position,
              demux_index: sample.demux_index,
            };
          }),
        };

        // Create or update the instrument run
        const createdRun = isUpdating
          ? // Always use the centralized draft endpoint builder for instrument run drafts (POST/PUT)
            await api.put<InstrumentRunPublic>(
              instrumentRunQueryKeys.endpoints.draft(selectedOrg.id),
              submitInstrumentRunData,
            )
          : await api.post<InstrumentRunPublic>(
              instrumentRunQueryKeys.endpoints.draft(selectedOrg.id),
              submitInstrumentRunData,
            );

        // Invalidate queries to ensure fresh data
        if (isUpdating && search.id) {
          await queryClient.invalidateQueries({
            queryKey: instrumentRunQueryKeys.detail(selectedOrg.id, search.id),
          });
        }
        await queryClient.invalidateQueries({
          queryKey: instrumentRunQueryKeys.list(selectedOrg.id),
        });

        // Show success message
        toast.success(`${value.name} ${isUpdating ? t('create:run.updated') : t('create:run.created')}`);

        // Reset form
        form.reset();

        // Navigate to the created instrument run
        if (createdRun?.id) {
          navigate({
            to: '/instrument_runs/$instrumentRunId',
            params: { instrumentRunId: createdRun.id },
          });
        } else {
          // Fallback to instrument runs list
          navigate({ to: '/instrument_runs' });
        }
      } catch (error) {
        handleInstrumentRunError(error, 'create:run.failed');
      }
    },
  });

  // Handle save as draft or updating run details:
  const handleUpdatingRunOrSavingDraft = async () => {
    if (!selectedOrg) {
      toast.error(t('create:run.noOrgSelected'));
      return;
    }

    const values = form.state.values;

    // For drafts, only validate name field (permissive validation)
    // type, species, and num_samples have defaults so are always present
    if (!values.name || values.name.trim() === '') {
      toast.error(t('create:run.validation.nameRequired'));
      return;
    }

    try {
      // Determine if we're updating a draft
      const isUpdatingDraft = mode === 'draft' && !!search.id;

      // Convert form data to API format for draft (permissive - samples are optional)
      const updatedInstrumentRunData: InstrumentRunDraftCreate = {
        name: values.name,
        description: values.description || null,
        type: values.type as InstrumentRunType,
        species: values.species as InstrumentRunSpecies,
        num_samples: Number(values.noOfSamples) as InstrumentRunSampleSize,
        samples: values.samples.map((sample: InstrumentRunDataRow) => {
          const sampleOrgId = sample.org_name ? orgNameToId.get(sample.org_name) : null;
          return {
            name: sample.name || null,
            study_name: sample.study_name || null,
            org_id: sampleOrgId || null,
            org_name: sample.org_name || null,
            well_position: sample.well_position || null,
            demux_index: sample.demux_index ?? null,
          };
        }),
      };

      // Create or update the run
      if ((formInstrumentRunData?.status === 'SUBMITTED' && search.id) || (isUpdatingDraft && search.id)) {
        // Always use the centralized draft endpoint builder for instrument run drafts (POST/PUT)
        await api.put<InstrumentRunPublic>(
          instrumentRunQueryKeys.endpoints.draft(selectedOrg.id),
          updatedInstrumentRunData,
        );

        // Invalidate the specific run query to ensure fresh data on next load
        await queryClient.invalidateQueries({
          queryKey: instrumentRunQueryKeys.detail(selectedOrg.id, search.id),
        });
      } else {
        // Always use the centralized draft endpoint builder for instrument run drafts (POST/PUT)
        await api.post<InstrumentRunPublic>(
          instrumentRunQueryKeys.endpoints.draft(selectedOrg.id),
          updatedInstrumentRunData,
        );
      }

      // Invalidate the list query to refresh the table
      await queryClient.invalidateQueries({
        queryKey: instrumentRunQueryKeys.list(selectedOrg.id),
      });

      // Show success message
      toast.success(`${values.name} ${t('create:run.savedAsDraft')}`);

      // Navigate to instrument runs list
      navigate({ to: '/instrument_runs' });
    } catch (error) {
      handleInstrumentRunError(error, 'create:run.failedToSaveDraft');
    }
  };

  // Reset form when initialValues change (e.g., when backend data is fetched)
  // Pass the new values to reset() to update both current values AND default values
  // This ensures the form and all child components (like SampleManager) get the fresh data
  useEffect(() => {
    form.reset(initialValues);
  }, [initialValues, form]);

  // Show message if no studies available
  if (studies.length === 0) {
    return (
      <Stack className={clsx(styles.container, globalStyles.scrollbars)}>
        <div className={styles.formContainer}>
          <div className={styles.messageContainer}>
            <Text>{t('create:errors.noStudiesAvailable')}</Text>

            <Button data-testid={'create-new-study-btn'}>{t('create:run.clickToCreateStudy')}</Button>
          </div>
        </div>
      </Stack>
    );
  }

  return (
    <Stack className={clsx(styles.container, globalStyles.scrollbars)} key={formKey}>
      <div className={styles.formContainer}>
        <form
          className={clsx(styles.form, irStyles.form)}
          onSubmit={async e => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <Text as="h1">
            {mode === 'draft'
              ? t('edit_draft', { ns: 'runs', defaultValue: 'Edit Draft' })
              : mode === 'edit'
                ? t('edit_run', { ns: 'runs', defaultValue: 'Edit Run' })
                : t('new_run', { ns: 'runs' })}
          </Text>

          <Stack className={irStyles.controlsContainer} fullWidth gap={6} orientation="vertical">
            {fieldConfigurations.map(field => (
              <form.Field key={field.name} name={field.name as keyof InstrumentRunFormValues}>
                {f => {
                  return (
                    <Fragment key={f.name}>
                      {field.type === 'text' && (
                        <Input
                          autoComplete="off"
                          // oxlint-disable-next-line no-autofocus If name, give autoFocus=true so it is immediately usable.
                          autoFocus={field?.name === 'name' ? true : undefined}
                          containerClassName={field.containerClassName}
                          className={styles.uiInput}
                          data-testid={`input-${field.name}`}
                          defaultValue={field.defaultValue || undefined}
                          id={field.name}
                          disabled={form.state.isSubmitting}
                          name={field.name}
                          onChange={({ target: { value } }) =>
                            form.setFieldValue(field.name as keyof InstrumentRunFormValues, value)
                          }
                          label={{
                            children: field.label,
                            htmlFor: field.name,
                            id: `label-${field.name}`,
                          }}
                          type={field.type}
                          value={f.state.value as string}
                        />
                      )}

                      {field.type === 'textarea' && (
                        <Textarea
                          autoComplete="off"
                          className={styles.uiInput}
                          containerClassName={field.containerClassName}
                          data-testid={`textarea-${field.name}`}
                          defaultValue={field.defaultValue || undefined}
                          id={field.name}
                          isDisabled={form.state.isSubmitting}
                          label={{
                            children: field.label,
                            htmlFor: field.name,
                            id: `label-${field.name}`,
                          }}
                          onChange={({ target }: { target: { value: string } }) =>
                            form.setFieldValue(field.name as keyof InstrumentRunFormValues, target.value)
                          }
                          rows={5}
                          value={f.state.value as string}
                        />
                      )}
                    </Fragment>
                  );
                }}
              </form.Field>
            ))}

            <Grid columns={3} fullWidth gap={4} rows={1}>
              {selectFields.map(field => (
                <form.Field key={field.name} name={field.name as keyof InstrumentRunFormValues}>
                  {f => {
                    // Calculate value outside JSX to avoid IIFE in props
                    const optionsArr = field.options as string[];
                    const optionIdx = optionsArr.indexOf(f.state.value as string);
                    const optionValue = optionIdx >= 0 ? optionIdx : 0; // fallback to first option
                    const label = {
                      children: field.label,
                      id: `label-${f.name}`,
                    };

                    return (
                      <Select
                        allowClear={false}
                        className={styles.select}
                        containerClassName={field.containerClassName}
                        closeOnSelect
                        data-testid={`select-${f.name}`}
                        isDisabled={form.state.isSubmitting}
                        id={f.name}
                        label={label}
                        onChange={idx => f.setValue(optionsArr[idx])}
                        onClear={() => f.setValue(optionsArr[0])}
                        options={optionsArr}
                        value={optionValue}
                      />
                    );
                  }}
                </form.Field>
              ))}
            </Grid>

            <div className={styles.noOfSamples}>
              <form.Subscribe selector={state => [state.values.noOfSamples]}>
                {([noOfSamples]) => (
                  <SampleManager form={form as unknown as InstrumentRunForm} noOfSamples={Number(noOfSamples)} />
                )}
              </form.Subscribe>
            </div>

            <div className={styles.footer}>
              <form.Subscribe selector={state => [state.isSubmitting, state.values]}>
                {([isSubmitting, values]) => {
                  // Type guards to ensure correct types
                  if (typeof values === 'boolean') {
                    return (
                      <Button data-testid="submit-btn" isDisabled={true} size="xl" type="submit" visual="primary">
                        {t('general:submit')}
                      </Button>
                    );
                  }

                  if (typeof isSubmitting !== 'boolean') {
                    return (
                      <Button data-testid="submit-btn" isDisabled={true} size="xl" type="submit" visual="primary">
                        {t('general:submit')}
                      </Button>
                    );
                  }

                  // Check if name field is filled
                  const nameFieldFilled = values.name && values.name.trim() !== '';

                  // Check if expected number of samples matches actual samples
                  const expectedSamples = Number(values.noOfSamples);
                  // Ensure all sample fields are strings for validation helpers
                  const samplesForValidation = values.samples.map((sample: InstrumentRunDataRow) => ({
                    ...sample,
                    study_name: sample.study_name ?? '',
                    org_name: sample.org_name ?? '',
                  }));

                  const correctSampleCount = isSampleCountCorrect(samplesForValidation, expectedSamples);

                  // Check if all sample fields are properly filled
                  const allSampleFieldsFilled = areAllSampleFieldsFilled(samplesForValidation);

                  // Enable submit only if all conditions are met
                  const isFormReadyToSubmit = nameFieldFilled && correctSampleCount && allSampleFieldsFilled;

                  const isDisabled =
                    !isFormReadyToSubmit || isSubmitting || formInstrumentRunData?.status === 'SUBMITTED';
                  // Draft save only requires name field (permissive validation)
                  // type, species, and num_samples have defaults so are always present
                  const isDraftSaveDisabled = !nameFieldFilled || isSubmitting;

                  return (
                    <Group orientation="horizontal">
                      <Button
                        data-testid="save-draft-btn"
                        isDisabled={isDraftSaveDisabled}
                        onPress={handleUpdatingRunOrSavingDraft}
                        size="xl"
                        type="button"
                        visual="success"
                      >
                        <SaveIcon size={buttonIconSize} /> {t('create:run.saveAsDraft')}
                      </Button>
                      <Button data-testid="submit-btn" isDisabled={isDisabled} size="xl" type="submit" visual="primary">
                        <CloudUploadIcon size={buttonIconSize} />{' '}
                        {isSubmitting ? t('create:run.creating') : t('general:submit')}
                      </Button>
                    </Group>
                  );
                }}
              </form.Subscribe>
            </div>
          </Stack>
        </form>
      </div>
    </Stack>
  );
}
